import React, { useState, useEffect } from 'react';
import EPaperList from './components/EPaperList';
import EPaperViewer from './components/EPaperViewer';
import BackToTop from './components/BackToTop';
import WhatsAppQR from './components/WhatsAppQR';
import SocialMedia from './components/SocialMedia';
import SEO from './components/SEO';
import { EPaper } from './types/epaper';
import { loadEPapers } from './services/epaperService';
import { MessageCircle } from 'lucide-react';
import './App.css';

function App() {
  const [epapers, setEpapers] = useState<EPaper[]>([]);
  const [selectedEPaper, setSelectedEPaper] = useState<EPaper | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showWhatsAppQR, setShowWhatsAppQR] = useState<boolean>(false);

  useEffect(() => {
    const fetchEPapers = async () => {
      try {
        setLoading(true);
        const data = await loadEPapers();
        setEpapers(data);
      } catch (err) {
        setError('Failed to load E-Papers. Please try again later.');
        console.error('Error fetching E-Papers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEPapers();
  }, []);

  const handleSelectEPaper = (epaper: EPaper) => {
    setSelectedEPaper(epaper);
  };

  const handleCloseViewer = () => {
    setSelectedEPaper(null);
  };

  const specialEditions = epapers.filter(epaper => epaper.type === 'special');
  const regularEpapers = epapers.filter(epaper => epaper.type === 'regular');

  if (error) {
    return (
      <div className="app-error">
        <div className="error-container">
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <SEO 
        title="The Brief - Namibia's Leading Business News E-Paper Archive | Daily Financial News"
        description="Access The Brief's complete digital e-paper archive. Namibia's #1 business and financial news publication. Daily economic updates, market analysis, and business insights from Windhoek. Free online access to premium business journalism."
        keywords="Namibia business news, The Brief e-paper, Windhoek financial news, Namibian economy, business newspaper Namibia, digital newspaper, financial journalism, economic news Africa, business insights Namibia, market analysis Windhoek"
        url="https://epaper.thebrief24.com/"
        tags={["Namibia", "Business", "Finance", "Economy", "Windhoek", "E-Paper", "Digital Newspaper", "Financial News"]}
      />
      <header className="app-header">
        <div className="container">
          <div className="header-content">
            <div className="header-logo-section">
              <a href="https://thebrief.com.na" target="_blank" rel="noopener noreferrer" className="logo-link">
                <img src="/logo.png" alt="The Brief Logo" className="logo-image" />
              </a>
            </div>
            <div className="header-actions">
              <SocialMedia />
              <button 
                className="whatsapp-qr-button"
                onClick={() => setShowWhatsAppQR(true)}
                title="Follow us on WhatsApp"
              >
                <MessageCircle size={20} />
                <span>WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="app-main">
        <div className="container">
          <EPaperList
            epapers={regularEpapers}
            specialEditions={specialEditions}
            onSelectEPaper={handleSelectEPaper}
            loading={loading}
          />
        </div>
      </main>
      
      {selectedEPaper && (
        <EPaperViewer
          epaper={selectedEPaper}
          onClose={handleCloseViewer}
        />
      )}
      
      {showWhatsAppQR && (
        <WhatsAppQR 
          isOpen={showWhatsAppQR}
          onClose={() => setShowWhatsAppQR(false)}
        />
      )}
      
      <footer className="app-footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} The Brief | All Rights Reserved</p>
        </div>
      </footer>

      <BackToTop />
    </div>
  );
}

export default App;
